import React, { useEffect, useRef, useState } from "react";
import { classNames } from "../../../shared/utility";
import { InputNumber } from "primereact/inputnumber";
import {
  CheckIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/20/solid";

export default function NumberInputText(props) {
  const {
    parentClass,
    label,
    name,
    onChange,
    onFocus,
    onBlur,
    value,
    labelOn,
    error,
    explanation,
    ref,
    labelClass,
    suffix,
    onCommit,
    disabled,
    inputClassName,
    saveStatus = "idle",
    min,
    ...other
  } = props;
  const usesCommit = typeof onCommit === "function";
  const normalizedValue =
    value === null || value === undefined || value === "" ? null : Number(value);
  const [draftValue, setDraftValue] = useState(normalizedValue);
  const [inputResetKey, setInputResetKey] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const draftValueRef = useRef(normalizedValue);
  const cancelCommit = useRef(false);

  useEffect(() => {
    if (!usesCommit) return;

    const nextValue =
      value === null || value === undefined || value === ""
        ? null
        : Number(value);
    if ((!isDirty && !isFocused) || draftValueRef.current === nextValue) {
      setIsDirty(false);
      draftValueRef.current = nextValue;
      setDraftValue(nextValue);
    }
  }, [isDirty, isFocused, usesCommit, value]);

  const commitValue = () => {
    if (!usesCommit) return;

    if (cancelCommit.current) {
      cancelCommit.current = false;
      return;
    }

    const valueToCommit = draftValueRef.current;
    if (disabled) {
      draftValueRef.current = normalizedValue;
      setDraftValue(normalizedValue);
      return;
    }

    if (valueToCommit === null && min === 0) {
      setIsDirty(true);
      draftValueRef.current = 0;
      setDraftValue(0);
      onCommit(0);
      return;
    }

    if (valueToCommit === null) return;

    if (valueToCommit !== normalizedValue) {
      onCommit(valueToCommit);
    } else {
      setIsDirty(false);
    }
  };

  const showSaveStatus = usesCommit && !disabled;
  const saveStatusLabel = {
    saving: "Saving",
    saved: "Saved",
    error: "Save failed",
  }[saveStatus];

  const saveStatusIndicator = {
    saving: (
      <span
        className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600"
        aria-hidden="true"
      />
    ),
    saved: (
      <CheckIcon className="h-4 w-4 text-green-600" aria-hidden="true" />
    ),
    error: (
      <ExclamationCircleIcon
        className="h-4 w-4 text-red-600"
        aria-hidden="true"
      />
    ),
  }[saveStatus];

  return (
    <div className={parentClass ? parentClass : null}>
      {labelOn ? (
        <div className="mb-1">
          <label
            htmlFor={name}
            className={classNames(
              labelClass ? labelClass : "text-sm",
              "block font-medium text-gray-900"
            )}
          >
            {label}
          </label>
        </div>
      ) : null}
      <div>
        <div className={showSaveStatus ? "flex items-center gap-1" : null}>
          <div className={showSaveStatus ? "min-w-0 flex-1" : null}>
            <InputNumber
              key={inputResetKey}
              name={name}
              value={usesCommit ? draftValue : value}
              onValueChange={(event) => {
                if (usesCommit) {
                  const nextValue = event.value ?? null;
                  setIsDirty(true);
                  draftValueRef.current = nextValue;
                  setDraftValue(nextValue);
                }
                onChange?.(event);
              }}
              onFocus={(event) => {
                setIsFocused(true);
                onFocus?.(event);
              }}
              onBlur={(event) => {
                setIsFocused(false);
                commitValue();
                onBlur?.(event);
              }}
              onKeyDown={(event) => {
                event.stopPropagation();

                if (!usesCommit) return;

                if (event.key === "Enter") {
                  event.preventDefault();
                  event.currentTarget.blur();
                }

                if (event.key === "Escape") {
                  event.preventDefault();
                  cancelCommit.current = true;
                  setIsDirty(false);
                  draftValueRef.current = normalizedValue;
                  setDraftValue(normalizedValue);
                  setInputResetKey((current) => current + 1);
                  event.currentTarget.blur();
                }
              }}
              ref={ref}
              suffix={suffix}
              disabled={disabled}
              min={min}
              inputClassName={classNames(
                inputClassName,
                saveStatus === "error" && "editable-table-input-error",
              )}
              {...other}
            />
          </div>
          {showSaveStatus ? (
            <span
              className="inline-flex h-4 w-4 shrink-0 items-center justify-center"
              role={saveStatusLabel ? "status" : undefined}
              aria-label={saveStatusLabel}
              title={saveStatusLabel}
            >
              {saveStatusIndicator}
            </span>
          ) : null}
        </div>

        {error ? <p className="font-medium text-red-500">{error}</p> : null}
      </div>
    </div>
  );
}
